import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { changePassword } from "@services/httpClient";
import { logout, selectUserInfo, updateUserInfo } from "@redux/slices/authSlice";
import { parseError } from "../utils/utils";
import { Eye, EyeOff, Lock, CheckCircle2, ShieldAlert } from "lucide-react";

const ForcePasswordReset = () => {
    const [formData, setFormData] = useState({
        old_password: "",
        new_password: "",
        confirm_new_password: "",
    });
    const [showPasswords, setShowPasswords] = useState({
        old: false,
        new: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userInfo = useSelector(selectUserInfo);

    React.useEffect(() => {
        if (userInfo && !userInfo.force_password_reset) {
            navigate("/");
        }
    }, [userInfo, navigate]);

    const validatePassword = (password) => {
        if (password.length < 8) return "Minimum 8 characters required";
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) {
            return "Must include A-Z, a-z, 0-9, and special char";
        }
        if (/(.)\1{2,}/.test(password)) {
            return "No more than 2 same characters continuously allowed";
        }
        return "";
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        
        setFormData(prev => ({ ...prev, [name]: newValue }));
        
        if (name === "new_password") {
            setErrors(prev => ({ ...prev, new_password: validatePassword(newValue) }));
        }
        if (name === "confirm_new_password") {
            setErrors(prev => ({ 
                ...prev, 
                confirm_new_password: newValue !== formData.new_password ? "Passwords do not match" : "" 
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check for spaces in any password field
        if (/\s/.test(formData.old_password) || /\s/.test(formData.new_password) || /\s/.test(formData.confirm_new_password)) {
            toast.error("Spaces are not allowed in passwords.");
            return;
        }
        
        const newPassError = validatePassword(formData.new_password);
        if (newPassError) {
            setErrors(prev => ({ ...prev, new_password: newPassError }));
            return;
        }

        if (formData.new_password === formData.old_password) {
            setErrors(prev => ({ ...prev, new_password: "New password must be different from current password" }));
            return;
        }

        if (formData.new_password !== formData.confirm_new_password) {
            setErrors(prev => ({ ...prev, confirm_new_password: "Passwords do not match" }));
            return;
        }

        setLoading(true);
        try {
            await changePassword(formData);
            toast.success("Password updated successfully! Welcome to Zyntegrate.");
            
            // Update local user info state to clear the flag
            dispatch(updateUserInfo({ force_password_reset: false }));
            
            // Redirect to home
            navigate("/");
        } catch (error) {
            toast.error(parseError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 tracking-tight">
                    Reset Your Password
                </h2>
                <p className="mt-2 text-center text-sm text-gray-500">
                    Please update your password to secure your account.
                </p>
            </div>

            <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-2xl sm:px-10 border border-gray-100">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 ml-1 mb-1">
                                Current Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPasswords.old ? "text" : "password"}
                                    name="old_password"
                                    required
                                    value={formData.old_password}
                                    onChange={handleInputChange}
                                    className="block w-full pl-4 pr-10 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 sm:text-sm transition-all font-medium"
                                    placeholder="Enter current password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    {showPasswords.old ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 ml-1 mb-1">
                                New Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPasswords.new ? "text" : "password"}
                                    name="new_password"
                                    required
                                    value={formData.new_password}
                                    onChange={handleInputChange}
                                    className={`block w-full pl-4 pr-10 py-3 border ${errors.new_password ? 'border-red-500' : 'border-gray-300'} rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 sm:text-sm transition-all font-medium`}
                                    placeholder="Create new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {errors.new_password && (
                                <p className="mt-1 ml-1 text-xs font-bold text-red-500 tracking-tight">
                                    {errors.new_password}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 ml-1 mb-1">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPasswords.confirm ? "text" : "password"}
                                    name="confirm_new_password"
                                    required
                                    value={formData.confirm_new_password}
                                    onChange={handleInputChange}
                                    className={`block w-full pl-4 pr-10 py-3 border ${errors.confirm_new_password ? 'border-red-500' : 'border-gray-300'} rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 sm:text-sm transition-all font-medium`}
                                    placeholder="Confirm new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {errors.confirm_new_password && (
                                <p className="mt-1 ml-1 text-xs font-bold text-red-500 tracking-tight">
                                    {errors.confirm_new_password}
                                </p>
                            )}
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-lg text-lg font-black text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                            >
                                {loading ? "Updating..." : "Update Password"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ForcePasswordReset;
